import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { resendConfirmationCode, useFirebaseAuth, signOut } from '../../../utils/firebase.js';

interface EmailVerificationProps {
  email: string;
  open: boolean;
}

const EmailVerification = ({
  email,
  open,
}: EmailVerificationProps) => {
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user } = useFirebaseAuth();
  const [, forceUpdate] = useState(0);

  // Poll for email verification status
  useEffect(() => {
    if (!open || !user) return;
    // Set up polling to check verification status
    const pollInterval = setInterval(async () => {
      try {
        // Reload the user to get fresh verification status
        await user.reload();

        // Force a re-render to reflect the updated user.emailVerified status
        forceUpdate(prev => prev + 1);
      } catch (error) {
        console.error('Error checking email verification status:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [open, user]);

  useEffect(() => {
    if (open && user?.emailVerified) {
      window.location.reload();
    }
  }, [user?.emailVerified, open])

  const handleResendVerification = async () => {
    setError('');
    setIsResending(true);
    setSuccessMessage('');

    try {
      await resendConfirmationCode();
      setSuccessMessage('✅ Verification email sent! Please check your inbox and spam folder.');
    } catch (err) {
      console.error('Resend verification error:', err);
      setError(err instanceof Error ? err.message : 'Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      window.location.reload();
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout. Please try again.');
      setIsLoggingOut(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true}
    >
      <DialogTitle>✉️ Verify Your Email Address</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography>
            We&apos;ve sent a verification link to <strong>{email}</strong>.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            📧 <strong>Check your inbox and spam folder</strong> for an email from us, then click the verification link to activate your account.
          </Typography>

          {user?.emailVerified ? (
            <Alert severity='success'>
              ✅ Email verified successfully! You can now continue using the application.
            </Alert>
          ) : (
            <Alert severity='info'>
              🔄 We&apos;re automatically checking for verification. Once you click the link, you&apos;ll be redirected automatically.
            </Alert>
          )}

          <Alert severity="warning">You must verify your email to continue using the application.</Alert>

          {error && <Alert severity="error">{error}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Didn&apos;t receive the email? Check your spam folder or click the button below to resend.
          </Typography>

          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Button
              variant="outlined"
              onClick={handleResendVerification}
              disabled={isResending || user?.emailVerified}
            >
              {user?.emailVerified ? (
                '✅ Email Verified'
              ) : isResending ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Sending...
                </>
              ) : (
                '📤 Resend Verification Email'
              )}
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={handleLogout}
              disabled={isLoggingOut || user?.emailVerified}
            >
              {isLoggingOut ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Logging out...
                </>
              ) : (
                '🚪 Wrong Email? Logout'
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default EmailVerification; 